<template>
  <themed-modal
    name="edit-task-modal"
    :title="isEditMode ? 'Edit Task' : 'New Task'"
    size="lg"
    :scrollable="true"
    :clickToClose="!loading"
  >
    <div class="edit-task-modal">
        <form @submit.prevent="submitTask" class="edit-task-modal__form">
          <div class="edit-task-modal__fields">
            <!-- Row 1: Type and Priority -->
            <div class="edit-task-modal__field">
              <label for="edit_task_category" class="edit-task-modal__label">
                Type <span class="edit-task-modal__required">*</span>
              </label>
              <select
                id="edit_task_category"
                v-model="form.task_category"
                class="edit-task-modal__select"
                required
              >
                <option value="">Select type...</option>
                <option value="Call">Call</option>
                <option value="Legal Review">Legal Review</option>
                <option value="Meeting">Meeting</option>
                <option value="Presentation">Presentation</option>
                <option value="Status Update">Status Update</option>
                <option value="Tour">Tour</option>
                <option value="Miscellaneous">Miscellaneous</option>
              </select>
              <div v-if="errors.task_category" class="edit-task-modal__error">
                {{ errors.task_category[0] }}
              </div>
            </div>
            
            <div class="edit-task-modal__field">
              <label for="edit_priority" class="edit-task-modal__label">
                Priority <span class="edit-task-modal__required">*</span>
              </label>
              <select
                id="edit_priority"
                v-model="form.priority"
                class="edit-task-modal__select"
                required
              >
                <option value="">Select priority...</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
              <div v-if="errors.priority" class="edit-task-modal__error">
                {{ errors.priority[0] }}
              </div>
            </div>
            
            <!-- Row 2: Start and End Date/Time -->
            <div class="edit-task-modal__field">
              <label for="edit_date_start" class="edit-task-modal__label">
                Start <span class="edit-task-modal__required">*</span>
              </label>
              <div class="edit-task-modal__date-time">
                <input
                  id="edit_date_start"
                  type="date"
                  v-model="form.date_start"
                  class="edit-task-modal__input"
                  required
                />
                <input
                  v-if="!form.is_all_day"
                  type="time"
                  v-model="form.time_start"
                  class="edit-task-modal__input"
                />
              </div>
              <div v-if="errors.date_time_start" class="edit-task-modal__error">
                {{ errors.date_time_start[0] }}
              </div>
            </div>
            
            <div class="edit-task-modal__field">
              <label for="edit_date_end" class="edit-task-modal__label">
                End <span class="edit-task-modal__required">*</span>
              </label>
              <div class="edit-task-modal__date-time">
                <input
                  id="edit_date_end"
                  type="date"
                  v-model="form.date_end"
                  class="edit-task-modal__input"
                  required
                />
                <input
                  v-if="!form.is_all_day"
                  type="time"
                  v-model="form.time_end"
                  class="edit-task-modal__input"
                />
              </div>
              <div v-if="errors.date_time_end" class="edit-task-modal__error">
                {{ errors.date_time_end[0] }}
              </div>
            </div>
            
            <!-- Row 3: Task Description (full width) -->
            <div class="edit-task-modal__field edit-task-modal__field--full">
              <label for="edit_task" class="edit-task-modal__label">
                Task <span class="edit-task-modal__required">*</span>
              </label>
              <input
                id="edit_task"
                type="text"
                v-model="form.task"
                class="edit-task-modal__input"
                placeholder="Enter task description..."
                required
              />
              <div v-if="errors.task" class="edit-task-modal__error">
                {{ errors.task[0] }}
              </div>
            </div>
            
            <!-- Row 4: Note (full width) -->
            <div class="edit-task-modal__field edit-task-modal__field--full">
              <label for="edit_note" class="edit-task-modal__label">Note</label>
              <textarea
                id="edit_note"
                v-model="form.note"
                class="edit-task-modal__textarea"
                rows="4"
                placeholder="Add additional notes..."
              ></textarea>
              <div v-if="errors.note" class="edit-task-modal__error">
                {{ errors.note[0] }}
              </div>
            </div>
            
            <!-- Row 5: Two Checkboxes -->
            <div class="edit-task-modal__field edit-task-modal__field--checkbox">
              <label class="edit-task-modal__checkbox-label">
                <input
                  type="checkbox"
                  v-model="form.is_all_day"
                  class="edit-task-modal__checkbox"
                />
                <span>All Day Task?</span>
              </label>
            </div>
            
            <div class="edit-task-modal__field edit-task-modal__field--checkbox">
              <label class="edit-task-modal__checkbox-label">
                <input
                  type="checkbox"
                  v-model="form.is_complete"
                  class="edit-task-modal__checkbox"
                />
                <span>Completed?</span>
              </label>
            </div>
          </div>
        </form>
      </div>

    <template #footer>
      <div >
        <themed-button
          variant="outline"
          @click="closeModal"
          :disabled="loading"
        >
          Cancel
        </themed-button>
        
        <themed-button
          variant="default"
          @click="submitTask"
          :disabled="loading"
        >
          <span v-if="loading" class="edit-task-modal__loading">
            <theme-icon icon="loader" type="icons" classes="w-4 h-4 animate-spin" />
          </span>
          <span v-else>{{ isEditMode ? 'Update Task' : 'Create Task' }}</span>
        </themed-button>
      </div>
    </template>
  </themed-modal>
</template>

<script>
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'EditTaskModal',
  
  components: {
    ThemedModal,
    ThemedButton,
    ThemeIcon
  },
  
  props: {
    projectId: {
      type: Number,
      required: true
    },
    task: {
      type: Object,
      default: null
    },
    isEditMode: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      form: this.initializeForm(),
      errors: {},
      loading: false
    };
  },
  
  watch: {
    task: {
      handler() {
        this.form = this.initializeForm();
        this.errors = {};
      },
      deep: true
    },
    isEditMode() {
      this.form = this.initializeForm();
      this.errors = {};
    }
  },
  
  mounted() {
    // Listen for modal opened event to reset/populate form
    this.$root.$on('modal::opened', (modalName) => {
      if (modalName === 'edit-task-modal') {
        this.form = this.initializeForm();
        this.errors = {};
      }
    });
  },
  
  beforeDestroy() {
    this.$root.$off('modal::opened');
  },
  
  methods: {
    initializeForm() {
      if (this.isEditMode && this.task) {
        // Edit mode - populate with task data
        return {
          id: this.task.id,
          project_id: this.task.project_id,
          task_category: this.task.task_category,
          is_all_day: this.task.is_all_day,
          date_start: this.task.start_date_formatted,
          time_start: this.task.start_time_formatted,
          date_end: this.task.end_date_formatted,
          time_end: this.task.end_time_formatted,
          priority: this.task.priority,
          task: this.task.task,
          note: this.task.note || '',
          is_complete: this.task.is_complete
        };
      } else {
        // Create mode - empty form
        return {
          project_id: this.projectId,
          task_category: '',
          is_all_day: false,
          date_start: null,
          time_start: null,
          date_end: null,
          time_end: null,
          priority: '',
          task: '',
          note: '',
          is_complete: false
        };
      }
    },
    
    validateForm() {
      this.errors = {};
      let isValid = true;
      
      if (!this.form.task_category) {
        this.errors.task_category = ['Task category is required'];
        isValid = false;
      }
      
      if (!this.form.date_start) {
        this.errors.date_time_start = ['Start date is required'];
        isValid = false;
      }
      
      if (!this.form.date_end) {
        this.errors.date_time_end = ['End date is required'];
        isValid = false;
      }
      
      if (!this.form.priority) {
        this.errors.priority = ['Priority is required'];
        isValid = false;
      }
      
      if (!this.form.task || this.form.task.trim() === '') {
        this.errors.task = ['Task description is required'];
        isValid = false;
      }
      
      // Validate start date is before or equal to end date
      if (this.form.date_start && this.form.date_end) {
        const startDate = new Date(this.form.date_start);
        const endDate = new Date(this.form.date_end);
        
        if (startDate > endDate) {
          this.errors.date_time_end = ['End date must be after start date'];
          isValid = false;
        }
      }
      
      return isValid;
    },
    
    buildDateTime(date, time) {
      if (!date) return null;
      
      if (this.form.is_all_day || !time) {
        return date;
      }
      
      return `${date} ${time}`;
    },
    
    async submitTask() {
      if (!this.validateForm()) {
        return;
      }
      
      this.loading = true;
      this.errors = {};
      
      try {
        // Build datetime strings
        const payload = {
          ...this.form,
          date_time_start: this.buildDateTime(this.form.date_start, this.form.time_start),
          date_time_end: this.buildDateTime(this.form.date_end, this.form.time_end)
        };
        
        // Remove separate date/time fields
        delete payload.date_start;
        delete payload.time_start;
        delete payload.date_end;
        delete payload.time_end;
        
        let response;
        
        if (this.isEditMode && this.task) {
          // Update existing task
          response = await axios.patch(
            `/project/${this.form.project_id}/tasks/${this.form.id}`,
            payload
          );
          
          // Emit success event with updated task
          this.$emit('task-updated', response.data.task);
        } else {
          // Create new task
          response = await axios.post(
            `/project/${this.projectId}/tasks`,
            payload
          );
          
          // Emit success event with new task
          this.$emit('task-created', response.data.task);
        }
      } catch (error) {
        console.error('Error updating task:', error);
        
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Failed to update task'
          });
        }
      } finally {
        this.loading = false;
      }
    },
    
    closeModal() {
      if (!this.loading) {
        this.$modal.hide('edit-task-modal');
      }
    }
  }
};
</script>
